import Button from '@/common/components/Button'
import Modal from '@/common/components/Modal'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useContractEnvelopeSendToRemoteSignService } from '@/features/privateCompanyLeadsFunnel/hooks/useContractEnvelopeSendToRemoteSignService'
import { ContractEnvelopeData } from '@/features/privateCompanyLeadsFunnel/types/privateCompanyLeadContractEnvelope'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronLastIcon } from 'lucide-react'
import { Dispatch, SetStateAction, useState } from 'react'

interface ModalSendContractEnvelopeToRemoteSigningServiceProps {
  privateCompanyLeadId: string
  contractEnvelope?: ContractEnvelopeData
  setMessages: Dispatch<SetStateAction<string[] | boolean>>
}

export const ModalSendContractEnvelopeToRemoteSigningService = ({
  privateCompanyLeadId,
  contractEnvelope,
  setMessages,
}: ModalSendContractEnvelopeToRemoteSigningServiceProps) => {
  const queryClient = useQueryClient()

  const { getUserPermission } = useUserPermissions()

  const [isOpen, setIsOpen] = useState(false)

  const userHasPermissionToSendContractToRemoteSigningService = getUserPermission(
    'Admin::Funnel::PrivateCompanyLeads::SendContractToRemoteSigningService',
  )?.properties?.permitted_states?.includes('quoting_finalization')

  const onSuccess = () => {
    setIsOpen(false)
    queryClient.invalidateQueries({ queryKey: ['privateCompanyLead', privateCompanyLeadId] })
    queryClient.invalidateQueries({ queryKey: ['privateCompanyLeadContractEnvelope', privateCompanyLeadId] })
  }

  const { mutate: sendContractToRemoteSignService, isPending: sendContractToRemoteSignServiceIsPending } =
    useContractEnvelopeSendToRemoteSignService({
      onSuccess,
      onError: FormTransformers.errorsTransformer({ setMessages }),
    })

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      Trigger={
        <Button
          type="button"
          name="send_contract_to_remote_signing_service"
          className="flex items-center gap-x-2 self-end"
          disabled={
            !userHasPermissionToSendContractToRemoteSigningService ||
            !contractEnvelope?.file_to_sign?.url ||
            !contractEnvelope?.contractee?.email ||
            !contractEnvelope?.contractor?.email ||
            !contractEnvelope?.witnesses?.length
          }
          isLoading={sendContractToRemoteSignServiceIsPending}
          tooltipMessage={!userHasPermissionToSendContractToRemoteSigningService ? 'Você não tem permissão' : undefined}
        >
          <p className="text-sm">Enviar para assinatura</p>

          <ChevronLastIcon className="size-4" />
        </Button>
      }
      triggerAsChild
      useCancellableFooter
      showClose={false}
      title="Você tem certeza?"
      titleClass="text-2xl"
      subtitle="O Link para assinatura do contrato será enviado no email para todos os associados, você poderá re-enviar para todos se necessário."
      subtitleClassName="mt-1 text-sm"
      confirmButtonText="Enviar contrato para assinatura"
      cancelButtonClassName="text-sm"
      confirmButtonClassName="text-sm bg-primary-blue"
      confirmButtonIsLoading={sendContractToRemoteSignServiceIsPending}
      onCancel={() => setIsOpen(false)}
      onConfirm={() => sendContractToRemoteSignService(privateCompanyLeadId)}
    />
  )
}
